import type {
    InstructorMonthlyPayoutLineDTO,
    InstructorSessionByInstructorItemDTO,
} from "../api/client";

export function payoutLocationLabel(
  location: InstructorMonthlyPayoutLineDTO["payoutLocationKind"],
): string {
  if (location === "off_site_near") return "ນອກສະຖານທີ່ໃກ້";
  if (location === "off_site_far") return "ນອກສະຖານທີ່ໄກ";
  if (location === "off_site") return "ນອກສະຖານທີ່";
  return "ໃນສະຖານທີ່";
}

export function payoutKindLabel(
  workKind: InstructorMonthlyPayoutLineDTO["payoutWorkKind"],
  location: InstructorMonthlyPayoutLineDTO["payoutLocationKind"],
): string {
  const loc =
    workKind === "partner" || workKind === "room_rental_assist"
      ? ""
      : ` · ${payoutLocationLabel(location)}`;
  if (workKind === "general_class") return `ຄລາດສ໌ທົ່ວໄປ${loc}`;
  if (workKind === "private_lesson") return `ສ່ວນຕົວ${loc}`;
  if (workKind === "room_rental_assist") {
    return `ຊ່ວຍສອນເຊົ່າຫ້ອງ${loc}`;
  }
  if (workKind === "general_class_assist") {
    return `ຊ່ວຍສອນຄລາດສ໌ (TA)${loc}`;
  }
  return "Partner";
}

export function sessionLocationLabel(
  sessionKind: InstructorMonthlyPayoutLineDTO["sessionLocationKind"],
  payoutLocation?: InstructorMonthlyPayoutLineDTO["payoutLocationKind"],
): string {
  if (sessionKind === "studio") return "ໃນສະຖານທີ່";
  if (
    payoutLocation === "off_site_near" ||
    payoutLocation === "off_site_far"
  ) {
    return payoutLocationLabel(payoutLocation);
  }
  return "ນອກສະຖານທີ່";
}

export function sourceTypeLabel(
  sourceType: InstructorMonthlyPayoutLineDTO["sourceType"],
): string {
  if (sourceType === "class_session") return "ຄລາດສ໌";
  if (sourceType === "room_booking") return "ເຊົ່າຫ້ອງ";
  return "ວິຊາສ່ວນຕົວ";
}

export function formatLinePayoutFormula(
  line: InstructorMonthlyPayoutLineDTO,
): string {
  const unit = line.payoutBasis === "per_session" ? "ຄັ້ງ" : "ຊມ.";
  const calculated =
    line.payoutBasis === "per_session"
      ? `1 ${unit} × ${line.amountKip.toLocaleString("lo-LA")} ₭`
      : `${line.hours.toLocaleString("lo-LA", {
          maximumFractionDigits: 2,
        })} ${unit} × ${line.amountKip.toLocaleString("lo-LA")} ₭`;
  if (line.isAmountOverridden) {
    return `ແກ້ຍອດ ${line.linePayoutKip.toLocaleString("lo-LA")} ₭ (ຄິດໄລ່ ${calculated})`;
  }
  return calculated;
}

export type PayoutBatchWorkflowStep = "review" | "confirmed" | "paid";

export function resolvePayoutBatchWorkflowStep(
  savedBatch: { status: "confirmed" | "paid" } | null | undefined,
): PayoutBatchWorkflowStep {
  if (!savedBatch) return "review";
  if (savedBatch.status === "paid") return "paid";
  return "confirmed";
}

export function formatPayoutKip(n: number): string {
  if (n <= 0) return "—";
  return `${n.toLocaleString("lo-LA")} ₭`;
}

export function formatHours(n: number): string {
  if (n <= 0) return "—";
  return n.toLocaleString("lo-LA", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

export function formatRateLabel(
  amountKip: number,
  basis: "per_hour" | "per_session",
): string {
  if (amountKip <= 0) return "—";
  const unit = basis === "per_session" ? "ຄັ້ງ" : "ຊມ.";
  return `${amountKip.toLocaleString("lo-LA")}/${unit}`;
}

export type InstructorPayoutKindBreakdown = {
  key: string;
  title: string;
  sessions: number;
  hours: number;
  payoutKip: number;
  studioSessions?: number;
  offSiteNearSessions?: number;
  offSiteFarSessions?: number;
  offSiteNearHours?: number;
  offSiteFarHours?: number;
  offSiteNearPayoutKip?: number;
  offSiteFarPayoutKip?: number;
  rateOffSiteNear?: string;
  rateOffSiteFar?: string;
  /** @deprecated use offSiteNear/Far for private lesson */
  offSiteSessions?: number;
  studioHours?: number;
  offSiteHours?: number;
  studioPayoutKip?: number;
  offSitePayoutKip?: number;
  rateStudio?: string;
  rateOffSite?: string;
  footnote?: string;
};

export function buildInstructorPayoutKindBreakdowns(
  row: InstructorSessionByInstructorItemDTO,
): InstructorPayoutKindBreakdown[] {
  const items: InstructorPayoutKindBreakdown[] = [];

  if (
    row.generalClassSessions > 0 ||
    row.estimatedPayoutGeneralClass > 0 ||
    row.billableHoursGeneralClass > 0
  ) {
    items.push({
      key: "general_class",
      title: "ຄລາດສ໌ທົ່ວໄປ",
      sessions: row.generalClassSessions,
      hours: row.billableHoursGeneralClass,
      payoutKip: row.estimatedPayoutGeneralClass,
      studioSessions: row.studioClassSessions,
      offSiteSessions: row.offSiteClassSessions,
      studioHours: row.billableHoursGeneralClassStudio,
      offSiteHours: row.billableHoursGeneralClassOffSite,
      studioPayoutKip: row.estimatedPayoutGeneralClassStudio,
      offSitePayoutKip: row.estimatedPayoutGeneralClassOffSite,
      rateStudio: formatRateLabel(
        row.generalClassRatePerHour,
        row.generalClassPayoutBasis,
      ),
      rateOffSite: formatRateLabel(
        row.generalClassOffSiteRatePerHour,
        row.generalClassOffSitePayoutBasis,
      ),
    });
  }

  if (
    row.privateLessonSessions > 0 ||
    row.estimatedPayoutPrivateLesson > 0 ||
    row.billableHoursPrivateLesson > 0
  ) {
    const nearSessions = row.offSiteNearPrivateLessonSessions ?? 0;
    const farSessions = row.offSiteFarPrivateLessonSessions ?? 0;
    const nearHours = row.billableHoursPrivateLessonOffSiteNear ?? 0;
    const farHours = row.billableHoursPrivateLessonOffSiteFar ?? 0;
    const nearPayout = row.estimatedPayoutPrivateLessonOffSiteNear ?? 0;
    const farPayout = row.estimatedPayoutPrivateLessonOffSiteFar ?? 0;

    items.push({
      key: "private_lesson",
      title: "ວິຊາສ່ວນຕົວ",
      sessions: row.privateLessonSessions,
      hours: row.billableHoursPrivateLesson,
      payoutKip: row.estimatedPayoutPrivateLesson,
      studioSessions: row.studioPrivateLessonSessions,
      studioHours: row.billableHoursPrivateLessonStudio,
      studioPayoutKip: row.estimatedPayoutPrivateLessonStudio,
      offSiteNearSessions: nearSessions,
      offSiteFarSessions: farSessions,
      offSiteNearHours: nearHours,
      offSiteFarHours: farHours,
      offSiteNearPayoutKip: nearPayout,
      offSiteFarPayoutKip: farPayout,
      rateStudio: formatRateLabel(
        row.privateLessonRatePerHour,
        row.privateLessonPayoutBasis,
      ),
      rateOffSiteNear: formatRateLabel(
        row.privateLessonOffSiteRatePerHour,
        row.privateLessonOffSitePayoutBasis,
      ),
      rateOffSiteFar: formatRateLabel(
        row.privateLessonOffSiteFarRatePerHour ?? row.privateLessonOffSiteRatePerHour,
        row.privateLessonOffSiteFarPayoutBasis ?? row.privateLessonOffSitePayoutBasis,
      ),
    });
  }

  if (
    row.partnerInstructorOnlyClassSessions > 0 ||
    row.estimatedPayoutPartner > 0 ||
    row.billableHoursPartner > 0
  ) {
    items.push({
      key: "partner",
      title: "Partner",
      sessions: row.partnerInstructorOnlyClassSessions,
      hours: row.billableHoursPartner,
      payoutKip: row.estimatedPayoutPartner,
      rateStudio: formatRateLabel(
        row.partnerRatePerHour,
        row.partnerPayoutBasis,
      ),
    });
  }

  if (
    (row.roomRentalAssistSessions ?? 0) > 0 ||
    (row.estimatedPayoutRoomRentalAssist ?? 0) > 0
  ) {
    items.push({
      key: "room_rental_assist",
      title: "ຊ່ວຍສອນເຊົ່າຫ້ອງ",
      sessions: row.roomRentalAssistSessions ?? 0,
      hours: 0,
      payoutKip: row.estimatedPayoutRoomRentalAssist ?? 0,
      rateStudio: formatRateLabel(
        row.roomRentalAssistRatePerSession ?? 0,
        row.roomRentalAssistPayoutBasis ?? "per_session",
      ),
    });
  }

  if (
    (row.generalClassAssistSessions ?? 0) > 0 ||
    (row.estimatedPayoutGeneralClassAssist ?? 0) > 0
  ) {
    items.push({
      key: "general_class_assist",
      title: "ຊ່ວຍສອນຄລາດສ໌ (TA)",
      sessions: row.generalClassAssistSessions ?? 0,
      hours: 0,
      payoutKip: row.estimatedPayoutGeneralClassAssist ?? 0,
      studioSessions: row.generalClassAssistStudioSessions ?? 0,
      offSiteSessions: row.generalClassAssistOffSiteSessions ?? 0,
      studioPayoutKip: row.estimatedPayoutGeneralClassAssistStudio ?? 0,
      offSitePayoutKip: row.estimatedPayoutGeneralClassAssistOffSite ?? 0,
      rateStudio: formatRateLabel(
        row.generalClassAssistStudioRatePerSession ?? 0,
        row.generalClassAssistStudioPayoutBasis ?? "per_session",
      ),
      rateOffSite: formatRateLabel(
        row.generalClassAssistOffSiteRatePerSession ?? 0,
        row.generalClassAssistOffSitePayoutBasis ?? "per_session",
      ),
    });
  }

  return items;
}

export function aggregateMonthlyPayoutTotals(
  rows: InstructorSessionByInstructorItemDTO[],
) {
  return rows.reduce(
    (acc, row) => ({
      instructors: acc.instructors + 1,
      sessions: acc.sessions + row.sessionsTaught,
      hours: acc.hours + row.billableHoursTotal,
      payout: acc.payout + row.estimatedPayout,
      generalClassPayout: acc.generalClassPayout + row.estimatedPayoutGeneralClass,
      privateLessonPayout:
        acc.privateLessonPayout + row.estimatedPayoutPrivateLesson,
      partnerPayout: acc.partnerPayout + row.estimatedPayoutPartner,
      roomRentalAssistPayout:
        acc.roomRentalAssistPayout + (row.estimatedPayoutRoomRentalAssist ?? 0),
      generalClassAssistPayout:
        acc.generalClassAssistPayout +
        (row.estimatedPayoutGeneralClassAssist ?? 0),
    }),
    {
      instructors: 0,
      sessions: 0,
      hours: 0,
      payout: 0,
      generalClassPayout: 0,
      privateLessonPayout: 0,
      partnerPayout: 0,
      roomRentalAssistPayout: 0,
      generalClassAssistPayout: 0,
    },
  );
}

export type PayoutTableFilter =
  | "all"
  | "general_class"
  | "private_lesson"
  | "partner"
  | "room_rental_assist"
  | "general_class_assist"
  | "rate_issue";

export function hasInstructorPayoutRateIssue(
  row: InstructorSessionByInstructorItemDTO,
): boolean {
  return getInstructorPayoutRateWarnings(row).length > 0;
}

export function getInstructorPayoutRateWarnings(
  row: InstructorSessionByInstructorItemDTO,
): string[] {
  const warnings: string[] = [];

  if (
    (row.billableHoursGeneralClassStudio > 0 ||
      row.studioClassSessions > 0) &&
    row.generalClassRatePerHour <= 0
  ) {
    warnings.push("ຄລາດສ໌ Studio ບໍ່ມີອັດຕາ");
  }
  if (
    (row.billableHoursGeneralClassOffSite > 0 ||
      row.offSiteClassSessions > 0) &&
    row.generalClassOffSiteRatePerHour <= 0
  ) {
    warnings.push("ຄລາດສ໌ ນອກສະຖານທີ່ ບໍ່ມີອັດຕາ");
  }
  if (
    (row.billableHoursPrivateLessonStudio > 0 ||
      row.studioPrivateLessonSessions > 0) &&
    row.privateLessonRatePerHour <= 0
  ) {
    warnings.push("ສ່ວນຕົວ Studio ບໍ່ມີອັດຕາ");
  }
  const nearHours = row.billableHoursPrivateLessonOffSiteNear ?? 0;
  const nearSessions = row.offSiteNearPrivateLessonSessions ?? 0;
  if ((nearHours > 0 || nearSessions > 0) && row.privateLessonOffSiteRatePerHour <= 0) {
    warnings.push("ສ່ວນຕົວ ໃກ້ ບໍ່ມີອັດຕາ");
  }
  const farHours = row.billableHoursPrivateLessonOffSiteFar ?? 0;
  const farSessions = row.offSiteFarPrivateLessonSessions ?? 0;
  const farRate = row.privateLessonOffSiteFarRatePerHour ?? 0;
  if ((farHours > 0 || farSessions > 0) && farRate <= 0) {
    warnings.push("ສ່ວນຕົວ ໄກ ບໍ່ມີອັດຕາ");
  }
  if (
    (row.billableHoursPartner > 0 ||
      row.partnerInstructorOnlyClassSessions > 0) &&
    row.partnerRatePerHour <= 0
  ) {
    warnings.push("Partner ບໍ່ມີອັດຕາ");
  }
  if (
    (row.roomRentalAssistSessions ?? 0) > 0 &&
    (row.roomRentalAssistRatePerSession ?? 0) <= 0
  ) {
    warnings.push("ຊ່ວຍສອນເຊົ່າຫ້ອງ ບໍ່ມີອັດຕາ");
  }
  if (
    (row.generalClassAssistStudioSessions ?? 0) > 0 &&
    (row.generalClassAssistStudioRatePerSession ?? 0) <= 0
  ) {
    warnings.push("ຊ່ວຍສອນຄລາດສ໌ (TA) Studio ບໍ່ມີອັດຕາ");
  }
  if (
    (row.generalClassAssistOffSiteSessions ?? 0) > 0 &&
    (row.generalClassAssistOffSiteRatePerSession ?? 0) <= 0
  ) {
    warnings.push("ຊ່ວຍສອນຄລາດສ໌ (TA) ນອກສະຖານທີ່ ບໍ່ມີອັດຕາ");
  }
  if (
    warnings.length === 0 &&
    row.sessionsTaught > 0 &&
    row.estimatedPayout <= 0
  ) {
    warnings.push("ມີ ຊົ່ວໂມງຮຽນ ແຕ່ຄ່າຈ້າງ 0");
  }

  return warnings;
}

export function rowMatchesPayoutTableFilter(
  row: InstructorSessionByInstructorItemDTO,
  filter: PayoutTableFilter,
): boolean {
  switch (filter) {
    case "general_class":
      return row.generalClassSessions > 0 || row.estimatedPayoutGeneralClass > 0;
    case "private_lesson":
      return row.privateLessonSessions > 0 || row.estimatedPayoutPrivateLesson > 0;
    case "partner":
      return (
        row.partnerInstructorOnlyClassSessions > 0 || row.estimatedPayoutPartner > 0
      );
    case "room_rental_assist":
      return (
        (row.roomRentalAssistSessions ?? 0) > 0 ||
        (row.estimatedPayoutRoomRentalAssist ?? 0) > 0
      );
    case "general_class_assist":
      return (
        (row.generalClassAssistSessions ?? 0) > 0 ||
        (row.estimatedPayoutGeneralClassAssist ?? 0) > 0
      );
    case "rate_issue":
      return hasInstructorPayoutRateIssue(row);
    default:
      return true;
  }
}

export function countPayoutTableFilter(
  rows: InstructorSessionByInstructorItemDTO[],
  filter: Exclude<PayoutTableFilter, "all">,
): number {
  return rows.filter((r) => rowMatchesPayoutTableFilter(r, filter)).length;
}
